import React, { useState, useEffect } from 'react';
import api from '../api';
import toast from 'react-hot-toast';

const COLUMNS = [
  { key: 'not_called',     label: 'Not Called',     color: 'var(--text-3)',  bg: 'var(--surface-2)',  border: 'var(--border)' },
  { key: 'interested',     label: 'Interested',     color: 'var(--green)',   bg: 'var(--green-dim)',  border: 'var(--green-border)' },
  { key: 'call_later',     label: 'Call Later',     color: 'var(--violet)',  bg: 'var(--violet-dim)', border: 'var(--violet-border)' },
  { key: 'not_interested', label: 'Not Interested', color: 'var(--red)',     bg: 'var(--red-dim)',    border: 'var(--red-border)' },
  { key: 'wrong_number',   label: 'Wrong Number',   color: 'var(--text-3)',  bg: 'var(--surface-2)',  border: 'var(--border)' },
  { key: 'schedule_demo',  label: 'Demo Scheduled', color: 'var(--accent)',  bg: 'var(--accent-dim)', border: 'var(--accent-border)' },
];

export default function Pipeline() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/leads/my');
        setLeads(data);
      } catch (e) {
        toast.error('Failed to load leads');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const getStatus = (lead) => {
    if (!lead.callRecord || lead.callRecord.status === 'pending') return 'not_called';
    return lead.callRecord.status;
  };

  const grouped = COLUMNS.reduce((acc, col) => {
    acc[col.key] = leads.filter(l => getStatus(l) === col.key);
    return acc;
  }, {});

  if (loading) return (
    <div className="fadein">
      <div className="page-header"><div className="page-title">Pipeline</div></div>
      <div className="empty"><div className="empty-icon"></div><p>Loading pipeline...</p></div>
    </div>
  );

  return (
    <div className="fadein">
      <div className="page-header">
        <div>
          <div className="page-title">Lead Pipeline</div>
          <div className="page-subtitle">{leads.length} total leads</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 12 }}>
        {COLUMNS.map(col => {
          const colLeads = grouped[col.key] || [];
          return (
            <div key={col.key} style={{ minWidth: 210, flex: '0 0 210px' }}>
              {/* Column header */}
              <div style={{
                padding: '9px 12px', background: col.bg,
                border: `1px solid ${col.border}`,
                borderRadius: 'var(--r)', marginBottom: 8,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
              }}>
                <span style={{ fontWeight: 600, fontSize: 12.5, color: col.color }}>{col.label}</span>
                <span style={{
                  background: col.color, color: '#fff', borderRadius: 99,
                  padding: '1px 7px', fontSize: 11.5, fontWeight: 700
                }}>{colLeads.length}</span>
              </div>

              {/* Lead cards */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {colLeads.map(lead => (
                  <div
                    key={lead._id}
                    onClick={() => setSelected(lead)}
                    className="card"
                    style={{ padding: '10px 12px', cursor: 'pointer', transition: 'box-shadow 0.12s' }}
                    onMouseEnter={e => e.currentTarget.style.boxShadow = 'var(--shadow-md)'}
                    onMouseLeave={e => e.currentTarget.style.boxShadow = 'var(--shadow-xs)'}
                  >
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2, color: 'var(--text-1)' }}>{lead.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{lead.number}</div>
                    {lead.city && <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>{lead.city}</div>}
                  </div>
                ))}
                {colLeads.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '18px 0', color: 'var(--text-3)', fontSize: 12 }}>
                    Empty
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Detail modal */}
      {selected && (
        <div className="modal-overlay" onClick={() => setSelected(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <div className="modal-head">
              <h2>{selected.name}</h2>
              <button className="modal-close" onClick={() => setSelected(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="info-row">
                <span className="info-label">Phone</span>
                <span className="info-value">{selected.number}</span>
              </div>
              <div className="info-row">
                <span className="info-label">City</span>
                <span className="info-value">{selected.city || '—'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Batch</span>
                <span className="info-value">{selected.batchName || '—'}</span>
              </div>
              {selected.callRecord && (
                <>
                  <div className="info-row">
                    <span className="info-label">Status</span>
                    <span className="info-value">
                      <span className={`badge badge-${selected.callRecord.status}`}>
                        {selected.callRecord.status.replace(/_/g, ' ')}
                      </span>
                    </span>
                  </div>
                  {selected.callRecord.notes && (
                    <div className="info-row">
                      <span className="info-label">Notes</span>
                      <span className="info-value">{selected.callRecord.notes}</span>
                    </div>
                  )}
                  {selected.callRecord.scheduledDemoAt && (
                    <div className="info-row">
                      <span className="info-label">Demo At</span>
                      <span className="info-value">{new Date(selected.callRecord.scheduledDemoAt).toLocaleString()}</span>
                    </div>
                  )}
                  {selected.callRecord.demoDetails && (
                    <div className="info-row">
                      <span className="info-label">Demo Details</span>
                      <span className="info-value">{selected.callRecord.demoDetails}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
